import { Button, DatePicker, Input, Select, Table, Typography, Tag, notification, Card, Row, Col } from "antd";
import { Link } from "react-router-dom";
import { EyeOutlined, SearchOutlined, CrownOutlined, DollarOutlined, UserOutlined, CheckCircleOutlined, ClockCircleOutlined, BankOutlined, CreditCardOutlined } from '@ant-design/icons';
import { useEffect, useState, useCallback, useRef } from "react";
import { motion } from "framer-motion";
import moment from "moment";
import subscriptionService from "../../../services/subscription-service";
import { calculateTableScrollX } from "../../../utils/widthCalculator";
import { useTableHover } from "../../../hooks/useTableHover";

const { Text } = Typography;
const { RangePicker } = DatePicker;

const Subscriptions = () => {
    useTableHover();

    const [loading, setLoading] = useState(false);
    const [dataSource, setDataSource] = useState({});
    const [stats, setStats] = useState({
        total_subscriptions: 0,
        active_subscriptions: 0,
        expired_subscriptions: 0,
        total_revenue: 0
    });
    const [filters, setFilters] = useState({
        search: '',
        status: 'all',
        start_date: null,
        end_date: null,
        sort_by: 'created_at',
        sort_order: 'desc',
        per_page: 15
    });
    const [searchValue, setSearchValue] = useState('');
    const debounceTimer = useRef(null);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const columns = [
        {
            title: "SNo",
            dataIndex: "id",
            key: "sno",
            width: 70,
            fixed: 'left',
            render: (text, record, index) => (
                <Text style={{ fontSize: '0.85rem' }}>{(dataSource.pagination.current_page - 1) * dataSource.pagination.per_page + index + 1}</Text>
            ),
        },
        {
            title: 'User',
            key: 'user',
            width: 200,
            fixed: 'left',
            render: (_, record) => (
                <div>
                    <Text style={{ fontSize: '0.85rem', fontWeight: 600 }}>
                        {record.user?.name || 'N/A'}
                    </Text>
                    <br />
                    <Text style={{ fontSize: '0.75rem', color: 'var(--text-gray)' }}>
                        {record.user?.email || ''}
                    </Text>
                </div>
            ),
        },
        {
            title: 'Platform',
            dataIndex: 'platform',
            key: 'platform',
            width: 120,
            render: (platform) => (
                <Tag color={platform === 'ios' ? 'blue' : platform === 'android' ? 'green' : 'orange'} style={{ textTransform: 'uppercase' }}>
                    {platform || 'N/A'}
                </Tag>
            ),
        },
        {
            title: 'Plan',
            dataIndex: 'name',
            key: 'plan_name',
            width: 150,
            render: (name) => <Text style={{ fontSize: '0.85rem' }}>{name || 'N/A'}</Text>,
        },
        {
            title: 'Price',
            dataIndex: 'price',
            key: 'price',
            width: 100,
            render: (price) => (
                <Text style={{ fontSize: '0.85rem', fontWeight: 600 }}>
                    ${price ? parseFloat(price).toFixed(2) : '0.00'}
                </Text>
            ),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: 120,
            render: (status) => {
                const color = status === 'active' ? 'green' : status === 'expired' ? 'red' : 'orange';
                return (
                    <Tag color={color} style={{ textTransform: 'uppercase' }}>
                        {status || 'N/A'}
                    </Tag>
                );
            },
        },
        {
            title: 'Purchased',
            dataIndex: 'purchased_at',
            key: 'purchased_at',
            width: 130,
            render: (date) => (
                <Text style={{ fontSize: '0.85rem' }}>
                    {date ? moment.utc(date).local().format('DD/MM/YYYY') : 'N/A'}
                </Text>
            ),
        },
        {
            title: 'Expires',
            dataIndex: 'expires_at',
            key: 'expires_at',
            width: 130,
            render: (date) => (
                <Text style={{ fontSize: '0.85rem' }}>
                    {date ? moment.utc(date).local().format('DD/MM/YYYY') : 'N/A'}
                </Text>
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 100,
            fixed: 'right',
            render: (_, record) => (
                <Link to={`/transactions/${record.id}`}>
                    <Button
                        type="text"
                        size="small"
                        style={{
                            padding: '4px 8px',
                            fontSize: '12px',
                            color: 'var(--primary-purple)',
                            border: '1px solid var(--primary-purple)',
                            borderRadius: 'var(--radius-md)',
                        }}
                        icon={<EyeOutlined style={{ fontSize: '12px' }} />}
                    />
                </Link>
            ),
        },
    ];

    const fetchSubscriptions = async () => {
        setLoading(true);
        try {
            const response = await subscriptionService.getSubscriptions(filters);
            if (response?.success) {
                // Handle nested structure: data.subscriptions and data.pagination
                const subscriptionsData = response.data || {};
                setDataSource({
                    data: subscriptionsData.subscriptions || [],
                    pagination: subscriptionsData.pagination || {},
                });
                // Extract stats from additionalData
                if (response.additionalData) {
                    setStats({
                        total_subscriptions: response.additionalData.total_subscriptions || 0,
                        active_subscriptions: response.additionalData.active_subscriptions || 0,
                        expired_subscriptions: response.additionalData.expired_subscriptions || 0,
                        total_revenue: response.additionalData.total_revenue || 0,
                    });
                }
            }
        } catch (error) {
            console.error('Error fetching subscriptions:', error);
            notification.error({
                message: 'Error',
                description: 'Failed to fetch subscriptions.',
            });
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = ({ search }) => {
        setFilters(prev => ({ ...prev, search }));
    };

    // Debounced search handler
    useEffect(() => {
        if (debounceTimer.current) {
            clearTimeout(debounceTimer.current);
        }

        debounceTimer.current = setTimeout(() => {
            handleSearch({ search: searchValue });
        }, 500);

        return () => {
            if (debounceTimer.current) {
                clearTimeout(debounceTimer.current);
            }
        };
    }, [searchValue]);

    useEffect(() => {
        fetchSubscriptions();
    }, [filters]);

    const handleChangePage = async (page) => {
        setFilters(prev => ({ ...prev, page }));
    };

    const handleStatusChange = (value) => {
        setFilters(prev => ({ ...prev, status: value }));
    };

    const handleDateChange = (dates) => {
        if (dates) {
            setFilters(prev => ({
                ...prev,
                start_date: dates[0].format('YYYY-MM-DD'),
                end_date: dates[1].format('YYYY-MM-DD'),
            }));
        } else {
            setFilters(prev => ({
                ...prev,
                start_date: null,
                end_date: null,
            }));
        }
    };

    // Listen for sidebar collapse state
    useEffect(() => {
        const handleSidebarCollapse = (event) => {
            setSidebarCollapsed(event.detail.collapsed);
        };

        window.addEventListener('sidebarCollapse', handleSidebarCollapse);
        return () => window.removeEventListener('sidebarCollapse', handleSidebarCollapse);
    }, []);

    // Calculate table scroll width
    // Table columns total: 70 + 200 + 100 + 150 + 100 + 120 + 130 + 130 + 80 = 1080px
    const calculateTableWidth = useCallback(() => {
        return calculateTableScrollX(sidebarCollapsed, 800, 1080, 40);
    }, [sidebarCollapsed]);

    const [tableWidth, setTableWidth] = useState(() => calculateTableScrollX(false, 800, 1080, 40));

    useEffect(() => {
        const handleResize = () => {
            setTableWidth(calculateTableWidth());
        };

        window.addEventListener('resize', handleResize);
        handleResize();

        return () => window.removeEventListener('resize', handleResize);
    }, [calculateTableWidth]);

    return (
        <div className={`page-container ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
            {/* Header Section */}
            <div className="module-header">
                <h1 className="module-title">Transaction Management</h1>
                <p className="module-subtitle">Manage and monitor all platform transactions</p>
            </div>

            {/* Statistics Cards */}
            {/* <div className="stats-grid">
                <div className="stats-card">
                    <div className="stats-value">{stats.total_subscriptions ?? 0}</div>
                    <div className="stats-label">Total Subscriptions</div>
                    <UserOutlined style={{ color: 'var(--primary-purple)', fontSize: '1.5rem', marginTop: 'var(--spacing-sm)' }} />
                </div>
                <div className="stats-card">
                    <div className="stats-value">{stats.active_subscriptions ?? 0}</div>
                    <div className="stats-label">Active Subscriptions</div>
                    <UserOutlined style={{ color: 'var(--text-gray)', fontSize: '1.5rem', marginTop: 'var(--spacing-sm)' }} />
                </div>
                <div className="stats-card">
                    <div className="stats-value">{stats.expired_subscriptions ?? 0}</div>
                    <div className="stats-label">Expired Subscriptions</div>
                    <CrownOutlined style={{ color: 'var(--accent-purple)', fontSize: '1.5rem', marginTop: 'var(--spacing-sm)' }} />
                </div>
                <div className="stats-card">
                    <div className="stats-value">{stats.total_revenue ?? 0}</div>
                    <div className="stats-label">Total Revenue</div>
                    <DollarOutlined style={{ color: 'var(--success-green)', fontSize: '1.5rem', marginTop: 'var(--spacing-sm)' }} />
                </div>
            </div> */}

            {/* Filters and Search */}
            <div className="filter-section">
                <div className="filter-row">
                    <div className="filter-item">
                        <Input
                            placeholder="Search by user name or email..."
                            prefix={<SearchOutlined style={{ color: 'var(--text-tertiary)' }} />}
                            value={searchValue}
                            onChange={(e) => setSearchValue(e.target.value)}
                            onPressEnter={(e) => {
                                setSearchValue(e.target.value);
                                handleSearch({ search: e.target.value });
                            }}
                            className="input-field"
                            allowClear
                        />
                    </div>
                    <div className="filter-item">
                        <Select
                            placeholder="Filter by status"
                            style={{ width: '100%' }}
                            className="input-field"
                            value={filters.status}
                            onChange={handleStatusChange}
                        >
                            <Select.Option value="all">All Status</Select.Option>
                            <Select.Option value="active">Active</Select.Option>
                            <Select.Option value="expired">Expired</Select.Option>
                            <Select.Option value="pending">Pending</Select.Option>
                        </Select>
                    </div>
                    <div className="filter-item">
                        <RangePicker
                            onChange={handleDateChange}
                            placeholder={['Start Date', 'End Date']}
                            style={{ width: '100%', backgroundColor: 'rgba(255,255,255,0.05)' }}
                            className="input-field"
                        />
                    </div>
                </div>
            </div>

            {/* Subscriptions Table */}
            <div className={`data-table-card ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
                <Table
                    columns={columns}
                    dataSource={dataSource.data}
                    className="data-table"
                    rowKey="id"
                    loading={loading}
                    pagination={{
                        onChange: handleChangePage,
                        current: dataSource.current_page,
                        pageSize: dataSource.per_page,
                        total: dataSource.total,
                        className: "data-table-pagination",
                        showSizeChanger: false,
                        showQuickJumper: false,
                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
                        size: 'small'
                    }}
                    scroll={{ x: tableWidth }}
                    size="small"
                    bordered={false}
                />
            </div>
        </div>
    );
};

export default Subscriptions;
